/**
 * JourneyAnimationPreview
 * -----------------------
 * Hidden preview route at /preview/journey-animation. Renders the animation
 * standalone on a clean background so it can be reviewed before integration
 * into NewLanding.jsx. NOT linked from anywhere; <meta robots noindex>.
 */
import React, { useEffect } from 'react';
import JourneyAnimation from '../components/hero/JourneyAnimation';

const JourneyAnimationPreview = () => {
  // Inject <meta robots="noindex,nofollow"> so the preview never ends up in
  // search results, even if someone shares the URL.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    const title = document.title;
    document.title = 'Journey Animation — Preview · Successlane';
    return () => {
      document.head.removeChild(meta);
      document.title = title;
    };
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#FFFBEB',
        padding: '32px 16px 64px',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0D1F3C', fontFamily: "'Fraunces', serif" }}>
              Hero Journey Animation — Preview
            </h1>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748B' }}>
              Internal review only. Not linked from any public page.
            </p>
          </div>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '4px 10px',
            background: '#FEF3C7', color: '#92400E', borderRadius: 99,
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>
            Preview
          </span>
        </div>

        {/* The actual animation, sized as it would appear in the hero */}
        <div style={{ marginBottom: 32 }}>
          <JourneyAnimation />
        </div>

        {/* Compact / mobile variant beneath */}
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Mobile (compact) variant
          </h2>
          <div style={{ maxWidth: 420 }}>
            <JourneyAnimation compact />
          </div>
        </div>

        {/* How to interact + checklist for the reviewer */}
        <div style={{ background: '#fff', border: '1px solid #E5E9F0', borderRadius: 12, padding: '20px 22px' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: '#0D1F3C', fontFamily: "'Fraunces', serif" }}>
            What to check
          </h2>
          <ul style={{ margin: 0, paddingLeft: 20, color: '#475569', fontSize: 13, lineHeight: 1.7 }}>
            <li>Loops through 6 stations (Door → Upload → Score → Pay → Recruiter → Mailbox) every ~24 seconds</li>
            <li>The character bobs while walking, pauses at each station while the room's mini-animation plays</li>
            <li>Clicking any station pauses the loop and opens an info-card with the step's description + a "Try it free" CTA</li>
            <li>"Continue tour" resumes the loop from where it paused</li>
            <li>The "Step N of 6 · {`{Title}`}" pill in the top-right updates as the character moves</li>
            <li>The compact variant below mirrors mobile layout</li>
            <li>Resize the browser window — the SVG scales fluidly</li>
            <li>OS-level "Reduce motion" setting (System Preferences → Accessibility → Display) freezes the scene at the Score frame</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default JourneyAnimationPreview;
